import React, { useState, useCallback, useMemo } from 'react';
import {
  Form,
  DatePicker,
  InputNumber,
  Button,
  Space,
  Row,
  Col,
  Table,
  message,
  Card,
  Typography,
  Tooltip,
  Popconfirm,
} from 'antd';
import {
  SaveOutlined,
  EditOutlined,
  DeleteOutlined,
  FilterOutlined,
  CloseOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import type { TableRowSelection } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import {
  useInfiniteBaselineForecasts,
  useMassSetBaselineForecasts,
  useDeleteBaselineForecasts,
  type RevenueItem,
  type RevenueCreateUpdate,
  type RevenueFilter,
} from '@/services';

interface EditingCell {
  id: number;
  field: string;
  value: number;
}

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

interface BaselineForecastTableProps {
  onRefresh: () => void;
}

const BaselineForecastTable: React.FC<BaselineForecastTableProps> = ({ onRefresh }) => {
  // Table state
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [dateFilter, setDateFilter] = useState<[string, string] | null>(null);
  const [bulkForecastValue, setBulkForecastValue] = useState<number>();

  // Build filter for API calls
  const apiFilter: RevenueFilter = useMemo(() => {
    const filter: RevenueFilter = {};
    
    if (dateFilter) {
      filter.date_gte = dateFilter[0];
      filter.date_lte = dateFilter[1];
    }
    
    return filter;
  }, [dateFilter]);

  // API hooks with infinite scroll
  const {
    data: infiniteData,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteBaselineForecasts(50, apiFilter, { field: 'date', order: 'ASC' });

  const massSetMutation = useMassSetBaselineForecasts();
  const deleteMutation = useDeleteBaselineForecasts();

  // Flatten all pages data into a single array
  const allBaselineForecasts = useMemo(() => {
    if (!infiniteData?.pages) return [];
    return infiniteData.pages.flatMap((page: any) => page.data || []);
  }, [infiniteData]);

  // Data is already filtered by the API based on apiFilter
  const filteredData = allBaselineForecasts;

  // Currency formatters
  const currencyFormatterWithSymbol = useCallback((value: any) => {
    if (!value) return '';
    return `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }, []);

  const currencyParser = useCallback((value: any) => {
    return Number(value?.replace(/\$\s?|(,*)/g, '')) || 0;
  }, []);

  // Handle scroll to load more data
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10;
    
    if (isNearBottom && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Handle date filter change
  const handleDateFilterChange = (dates: any) => {
    if (dates && dates[0] && dates[1]) {
      setDateFilter([
        dates[0].format('YYYY-MM-DD'),
        dates[1].format('YYYY-MM-DD')
      ]);
    } else {
      setDateFilter(null);
    }
  };

  // Handle cell editing
  const handleStartEdit = useCallback((record: RevenueItem, field: string) => {
    setEditingCell({
      id: record.id,
      field,
      value: record[field as keyof RevenueItem] as number,
    });
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingCell) return;

    try {
      // Find the original record to get the date
      const originalRecord = filteredData.find(item => item.id === editingCell.id);
      if (!originalRecord) {
        message.error('Record not found');
        return;
      }

      // Convert to the format expected by the admin API
      const updateData: RevenueCreateUpdate[] = [{
        id: editingCell.id,
        date: originalRecord.date,
        type: 'forecast_baseline' as const,
        amount: editingCell.field === 'amount' ? editingCell.value : originalRecord.amount,
      }];

      await massSetMutation.mutateAsync(updateData);
      
      setEditingCell(null);
      message.success('Baseline forecast updated successfully');
      onRefresh();
    } catch (error) {
      console.error('Error updating forecast:', error);
    }
  }, [editingCell, filteredData, massSetMutation, onRefresh]);

  const handleCancelEdit = useCallback(() => {
    setEditingCell(null);
  }, []);

  // Handle bulk operations
  const handleBulkUpdate = useCallback(async () => {
    if (selectedRowKeys.length === 0 || bulkForecastValue === undefined) {
      message.warning('Please select rows and enter a forecast value');
      return;
    }

    try {
      // Convert to the format expected by the admin API
      const updateData: RevenueCreateUpdate[] = selectedRowKeys.map(id => {
        const originalRecord = filteredData.find(item => item.id === id);
        if (!originalRecord) {
          throw new Error(`Record with id ${id} not found`);
        }
        
        return {
          id,
          date: originalRecord.date,
          type: 'forecast_baseline' as const,
          amount: bulkForecastValue,
        };
      });

      await massSetMutation.mutateAsync(updateData);
      
      setSelectedRowKeys([]);
      setBulkForecastValue(undefined);
      message.success(`Updated ${selectedRowKeys.length} baseline forecast(s)`);
      onRefresh();
    } catch (error) {
      console.error('Error bulk updating forecasts:', error);
    }
  }, [selectedRowKeys, bulkForecastValue, filteredData, massSetMutation, onRefresh]);

  const handleBulkDelete = useCallback(async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select rows to delete');
      return;
    }

    try {
      await deleteMutation.mutateAsync(selectedRowKeys);
      setSelectedRowKeys([]);
      message.success(`Deleted ${selectedRowKeys.length} baseline forecast(s)`);
      onRefresh();
    } catch (error) {
      console.error('Error bulk deleting forecasts:', error);
    }
  }, [selectedRowKeys, deleteMutation, onRefresh]);

  const rowSelection: TableRowSelection<RevenueItem> = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys as number[]),
    preserveSelectedRowKeys: true,
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date: string) => dayjs(date).format('MMM DD, YYYY'),
      sorter: (a: RevenueItem, b: RevenueItem) => dayjs(a.date).unix() - dayjs(b.date).unix(),
    },
    {
      title: 'Forecast',
      dataIndex: 'amount',
      key: 'amount',
      width: 150,
      render: (value: number, record: RevenueItem) => {
        const isEditing = editingCell?.id === record.id && editingCell?.field === 'amount';
        
        if (isEditing) {
          return (
            <Space>
              <InputNumber
                value={editingCell.value as number}
                onChange={(value) => setEditingCell({
                  ...editingCell,
                  value: value || 0,
                })}
                min={0}
                step={100}
                precision={2}
                size="small"
                style={{ width: 120 }}
                formatter={currencyFormatterWithSymbol}
                parser={currencyParser}
              />
              <Button
                type="primary"
                size="small"
                icon={<SaveOutlined />}
                onClick={handleSaveEdit}
                loading={massSetMutation.isPending}
              />
              <Button
                size="small"
                icon={<CloseOutlined />}
                onClick={handleCancelEdit}
              />
            </Space>
          );
        }

        return (
          <div 
            style={{ cursor: 'pointer', padding: '4px 8px', borderRadius: '4px' }}
            onClick={() => handleStartEdit(record, 'amount')} // Single-click instead of double-click
          >
            <Space>
              <Text>${value?.toLocaleString() || '0'}</Text>
              <EditOutlined style={{ fontSize: '12px', color: '#1890ff' }} />
            </Space>
          </div>
        );
      },
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => dayjs(date).format('MMM DD'),
      sorter: (a: RevenueItem, b: RevenueItem) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 120,
      render: (date: string) => dayjs(date).format('MMM DD'),
      sorter: (a: RevenueItem, b: RevenueItem) => dayjs(a.updatedAt).unix() - dayjs(b.updatedAt).unix(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: any, record: RevenueItem) => (
        <Space size="small">
          <Tooltip title="Delete">
            <Popconfirm
              title="Delete this forecast?"
              onConfirm={() => deleteMutation.mutateAsync([record.id]).then(() => onRefresh())}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                loading={deleteMutation.isPending}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Card 
      title={
        <Space>
          <FilterOutlined />
          <span>Existing Baseline Forecasts</span>
        </Space>
      }
    >
      {/* Filters */}
      <Form layout="inline" style={{ marginBottom: 16 }}>
        <Form.Item label="Date Range">
          <RangePicker
            onChange={handleDateFilterChange}
            placeholder={['Start Date', 'End Date']}
            format="MMM DD, YYYY"
            style={{ width: 250 }}
          />
        </Form.Item>
        <Form.Item>
          <Button
            icon={<ClearOutlined />}
            onClick={() => {
              setDateFilter(null);
            }}
          >
            Clear Filters
          </Button>
        </Form.Item>
      </Form>

      {/* Bulk Operations */}
      <Row justify="space-between" style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Text>Total: {filteredData.length} forecast(s)</Text>
        </Col>
        
        {selectedRowKeys.length > 0 && (
          <Col span={16}>
            <Text strong>Bulk Operations ({selectedRowKeys.length} selected):</Text>
            <Space style={{ marginTop: 4 }}>
              <InputNumber
                placeholder="New forecast"
                value={bulkForecastValue}
                onChange={(value) => setBulkForecastValue(value ?? undefined)}
                min={0}
                step={100}
                precision={2}
                style={{ width: 140 }}
                formatter={currencyFormatterWithSymbol}
                parser={currencyParser}
              />
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={handleBulkUpdate}
                loading={massSetMutation.isPending}
                disabled={!bulkForecastValue}
                size="small"
              >
                Update
              </Button>
              <Popconfirm
                title={`Delete ${selectedRowKeys.length} selected forecast(s)?`}
                onConfirm={handleBulkDelete}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  loading={deleteMutation.isPending}
                  size="small"
                >
                  Delete
                </Button>
              </Popconfirm>
            </Space>
          </Col>
        )}
      </Row>

      {/* Table */}
      <div style={{ maxHeight: 400, overflowY: 'auto' }} onScroll={handleScroll}>
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={false}
          size="small"
          rowKey="id"
          scroll={{ x: 800 }}
        />
        
        {isFetchingNextPage && (
          <div style={{ textAlign: 'center', padding: 16 }}>
            <Text type="secondary">Loading more...</Text>
          </div>
        )}
      </div>
    </Card>
  );
};

export default BaselineForecastTable;
